/**
 * `Skeleton` — segnaposto animato (shimmer) usato durante il caricamento dei
 * dati, al posto di uno spinner/messaggio testuale. Riproduce la "forma" del
 * contenuto in arrivo (blocchi, righe di testo, card) così il layout non salta
 * quando i dati arrivano.
 *
 * Accessibilità: puramente decorativo (`aria-hidden`). L'annuncio dello stato
 * di caricamento allo screen reader è responsabilità del contenitore, che deve
 * esporre una live region (es. un `role="status"` con testo). Vedi l'uso in
 * `Dashboard` dove lo skeleton è avvolto da un `role="status"` con testo
 * visivamente nascosto.
 */
import type { CSSProperties } from 'react';

interface SkeletonProps {
  /** Larghezza CSS (es. `'100%'`, `'8rem'`, `120`). Default: `'100%'`. */
  width?: number | string;
  /** Altezza CSS (es. `'1em'`, `40`). Default: `'1em'`. */
  height?: number | string;
  /** Forma: `'text'` (raggio piccolo), `'rect'` (raggio card), `'circle'`. */
  variant?: 'text' | 'rect' | 'circle';
  /** Raggio dei bordi custom; sovrascrive quello derivato dalla `variant`. */
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}

const RADIUS_BY_VARIANT: Record<NonNullable<SkeletonProps['variant']>, string> = {
  text: 'var(--radius-xs)',
  rect: 'var(--radius)',
  circle: 'var(--radius-pill)',
};

export function Skeleton({
  width = '100%',
  height = '1em',
  variant = 'text',
  radius,
  className,
  style,
}: SkeletonProps) {
  return (
    <span
      className={['ui-skeleton', className ?? ''].filter(Boolean).join(' ')}
      aria-hidden="true"
      style={{
        width,
        height,
        borderRadius: radius ?? RADIUS_BY_VARIANT[variant],
        ...style,
      }}
    />
  );
}
